import WebSocketManager from './websocket-manager';
import type { WebSocketManagerConfig } from './types';
import { api } from '../../generated/trades';
import { WS_TRACKING_EVENTS } from '../constants/nr-events';
import { WebsocketClientType } from '../constants/common';

// ============================================
// Types
// ============================================

export enum TradeEventTypes {
  ORDER = 'order',
  POSITION = 'position',
  GTT_ORDER = 'gtt_order',
  POSITION_REFRESH = 'position_refresh',
}

export enum TradeEventMode {
  ORDER = 'O',
  POSITION = 'PO',
  GTT_ORDER = 'G',
  POSITION_REFRESH = 'PO_REF',
}

export interface TradeData {
  subPath?: string;
  mode?: string;
  [key: string]: unknown;
}

export type TradesCallback = (
  message: TradeData | null,
  error?: unknown,
) => void;

interface ISubscriptions {
  path: string;
  callback: TradesCallback;
  subId: string;
  type: TradeEventTypes;
}

type TWebsocketPath = string;
type TSubscriptionKey = `${TWebsocketPath}-${TradeEventMode}`;

// Default protobuf decoder for trades
const defaultProtobufDecoder = (data: Uint8Array): TradeData => {
  const TradeDataClass = api.trades.TradeData;
  return TradeDataClass.decode(data) as unknown as TradeData;
};

export interface TradesWebSocketManagerConfig {
  websocketUrl: string;
  socketId: WebsocketClientType;
  protobufDecoder?: (data: Uint8Array) => TradeData;
  websocketConfig: WebSocketManagerConfig;
}

// ============================================
// TradesWebSocketManager Class
// ============================================

class TradesWebSocketManager extends WebSocketManager {
  private static tradesInstance: TradesWebSocketManager | null = null;
  private tradesWebSocketUrl: string;
  private static setConnectionCallback: (
    ws: WebSocket | null,
    client: string,
  ) => void = () => {};
  private socketId: WebsocketClientType = WebsocketClientType.TRADES;
  private subscriptions: Map<TSubscriptionKey, ISubscriptions[]> = new Map();
  private connectionStartTime: number = 0;
  private isConnectionInProgress: boolean = false;
  private lastOnMessageError: string = '';
  private protobufDecoder: (data: Uint8Array) => TradeData;
  private static trackEventFn:
    | ((eventName: string, data: Record<string, unknown>) => void)
    | null = null;

  private constructor(config: TradesWebSocketManagerConfig) {
    super(config.websocketConfig);
    this.tradesWebSocketUrl = config.websocketUrl;
    this.socketId = config.socketId;
    this.protobufDecoder = config.protobufDecoder || defaultProtobufDecoder;
    // Store trackEvent from analytics config
    if (config.websocketConfig.analytics?.trackEvent) {
      TradesWebSocketManager.trackEventFn =
        config.websocketConfig.analytics.trackEvent;
    }
  }

  private trackTradesEvent(
    eventName: string,
    data: Record<string, unknown>,
  ): void {
    if (TradesWebSocketManager.trackEventFn) {
      TradesWebSocketManager.trackEventFn(eventName, {
        ...data,
        client: this.socketId,
      });
    }
  }

  static getTradesInstance(
    config?: TradesWebSocketManagerConfig,
  ): TradesWebSocketManager {
    if (!this.tradesInstance) {
      if (!config) {
        throw new Error('Config is required for first initialization');
      }
      this.tradesInstance = new TradesWebSocketManager(config);
    }
    return this.tradesInstance;
  }

  static initializeTradesSocketManager(
    config: TradesWebSocketManagerConfig,
    setConnectionCallback: (ws: WebSocket | null, client: string) => void,
    hashToken: string | null = null,
  ): void {
    if (!this.tradesInstance) {
      this.tradesInstance = new TradesWebSocketManager(config);
    }
    // Track initialization event
    if (TradesWebSocketManager.trackEventFn) {
      TradesWebSocketManager.trackEventFn(
        WS_TRACKING_EVENTS.INIT_SOCKET_MANAGER,
        {
          client: config.socketId,
          hasHashToken: !!hashToken,
          websocketUrl: config.websocketUrl,
        },
      );
    }
    super.initialize(hashToken);
    TradesWebSocketManager.setConnectionCallback = setConnectionCallback;
  }

  static getTradesWSInstance(): TradesWebSocketManager | null {
    if (!this.tradesInstance) {
      return null;
    }
    return this.tradesInstance;
  }

  static resetInstance(): void {
    this.tradesInstance = null;
  }

  async initializeTradesConnection(): Promise<void> {
    try {
      if (this.isConnectionInProgress) {
        this.trackTradesEvent(
          WS_TRACKING_EVENTS.INIT_CONNECTION_ALREADY_IN_PROGRESS,
          {
            url: this.tradesWebSocketUrl,
          },
        );
        return;
      }
      this.isConnectionInProgress = true;
      this.trackTradesEvent(WS_TRACKING_EVENTS.INIT_CONNECTION_START, {
        url: this.tradesWebSocketUrl,
      });
      await super.initializeConnection(
        this.socketId,
        this.tradesWebSocketUrl,
        this.setupEventListeners.bind(this),
      );
      this.connectionStartTime = Date.now();
    } catch (error: unknown) {
      console.error(
        '[TradesWebSocketManager] Error initializing trades connection:',
        error,
      );
      this.trackTradesEvent(WS_TRACKING_EVENTS.INIT_CONNECTION_ERROR, {
        url: this.tradesWebSocketUrl,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      this.isConnectionInProgress = false;
    }
  }

  async disconnectTradesConnection(): Promise<void> {
    void super.disconnect(this.socketId);
  }

  getTradesConnection(): WebSocket | null {
    return super.getSocket(this.socketId);
  }

  private setupEventListeners() {
    const socket = this.getTradesConnection();

    if (!socket) {
      console.error(
        '[TradesWebSocketManager] Trades connection not initialized',
      );
      return;
    }

    socket.onopen = () => {
      this.subscriptions.clear();

      // Ensure socket is in the static Map (may have been cleared by cleanup/HMR)
      super.ensureSocketInMap(this.socketId, socket);

      TradesWebSocketManager.setConnectionCallback(socket, this.socketId);

      super.resetReconnectAttempts(this.socketId);

      const connectionTime = super.getConnectionCreationTime(this.socketId);
      this.trackTradesEvent(WS_TRACKING_EVENTS.CONNECTED, {
        connectionTime,
        url: this.tradesWebSocketUrl,
      });
    };

    socket.onclose = (event) => {
      this.trackTradesEvent(WS_TRACKING_EVENTS.DISCONNECTED, {
        wasClean: event.wasClean,
        code: event.code,
        reason: event.reason,
        url: this.tradesWebSocketUrl,
      });

      TradesWebSocketManager.setConnectionCallback(null, this.socketId);

      if (!event.wasClean) {
        super.handleReconnection(this.socketId, 'onclose');
      }
    };

    socket.onmessage = async (event: MessageEvent) => {
      try {
        const arrayBuffer = await (event.data instanceof Blob
          ? event.data.arrayBuffer()
          : event.data);

        const uint8Array = new Uint8Array(arrayBuffer);
        const decodedMessage = this.protobufDecoder(uint8Array);

        const subPath = decodedMessage?.subPath;
        const mode = decodedMessage?.mode as TradeEventMode;

        if (!subPath || !mode) {
          console.error(
            '[TradesWebSocketManager] Invalid message',
            decodedMessage,
          );
          this.trackTradesEvent(WS_TRACKING_EVENTS.MESSAGE_NO_SUBPATH, {
            message: JSON.stringify(decodedMessage),
          });
          return;
        }

        const subscriptionKeyFromMap = this.getSubscriptionKey(subPath, mode);
        const subscriptions = this.subscriptions.get(subscriptionKeyFromMap);

        if (subscriptions) {
          subscriptions.forEach((sub) => {
            try {
              sub.callback(decodedMessage);
            } catch (error: unknown) {
              console.error(
                '[TradesWebSocketManager] Error processing WebSocket message',
                error,
              );
              this.trackTradesEvent(WS_TRACKING_EVENTS.MESSAGE_CALLBACK_ERROR, {
                subPath,
                mode,
                error: error instanceof Error ? error.message : String(error),
              });
            }
          });
        }

        this.lastOnMessageError = '';
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        console.error(
          `[TradesWebSocketManager] Error processing WebSocket message --> ${this.socketId}`,
          errorMessage,
        );
        if (this.lastOnMessageError !== errorMessage) {
          this.lastOnMessageError = errorMessage;
          this.trackTradesEvent(WS_TRACKING_EVENTS.MESSAGE_PROCESSING_ERROR, {
            error: errorMessage,
          });
        }
      }
    };

    socket.onerror = (error) => {
      console.error(
        `[TradesWebSocketManager] WebSocket error --> ${this.socketId}`,
        error,
      );
      this.trackTradesEvent(WS_TRACKING_EVENTS.ERROR, {
        url: this.tradesWebSocketUrl,
        error: String(error),
      });

      super.handleReconnection(this.socketId, 'onerror');
    };
  }

  private getSubscriptionKey(
    path: TWebsocketPath,
    mode: TradeEventMode,
  ): TSubscriptionKey {
    return `${path}-${mode}`;
  }

  private getMode(type: TradeEventTypes): TradeEventMode {
    switch (type) {
      case TradeEventTypes.GTT_ORDER:
        return TradeEventMode.GTT_ORDER;
      case TradeEventTypes.POSITION:
        return TradeEventMode.POSITION;
      case TradeEventTypes.POSITION_REFRESH:
        return TradeEventMode.POSITION_REFRESH;
      default:
        return TradeEventMode.ORDER;
    }
  }

  private subscribe(
    path: TWebsocketPath,
    callback: TradesCallback,
    type: TradeEventTypes,
  ): () => void {
    try {
      const subId = `sub-${path}-trade-${type}-${Math.random()
        .toString(8)
        .slice(2, 11)}`;

      const socket = this.getTradesConnection();

      if (!socket) {
        this.trackTradesEvent(WS_TRACKING_EVENTS.SUBSCRIBE_NO_CONNECTION, {
          path,
          type,
        });
        throw new Error(`Trades connection not initialized for ${type}`);
      }

      const mode = this.getMode(type);
      const subscriptionKey = this.getSubscriptionKey(path, mode);
      const existingSubscriptions =
        this.subscriptions.get(subscriptionKey) ?? [];

      if (!existingSubscriptions.length) {
        if (socket.readyState === WebSocket.OPEN) {
          const subscribeMessage = {
            mode,
            action: 'SUB',
            entities: [`${path}`],
          };
          socket.send(JSON.stringify(subscribeMessage));
        }
      }

      this.subscriptions.set(subscriptionKey, [
        ...existingSubscriptions,
        { path, callback, subId, type },
      ]);

      this.trackTradesEvent(WS_TRACKING_EVENTS.SUBSCRIBE_SUCCESS, {
        path,
        type,
      });

      return () => {
        this.unsubscribe({ subscriptionKey, path, subId, type });
      };
    } catch (error: unknown) {
      console.error(
        '[TradesWebSocketManager] Error subscribing to trades',
        error,
      );
      this.trackTradesEvent(WS_TRACKING_EVENTS.SUBSCRIBE_ERROR, {
        path,
        type,
        error: error instanceof Error ? error.message : String(error),
      });
      return () => {};
    }
  }

  private unsubscribe({
    subscriptionKey,
    path,
    subId,
    type,
  }: {
    subscriptionKey: TSubscriptionKey;
    path: TWebsocketPath;
    subId: string;
    type: TradeEventTypes;
  }): void {
    try {
      const socket = this.getTradesConnection();
      const subs = this.subscriptions.get(subscriptionKey);

      if (subs) {
        const filteredSubs = subs.filter((sub) => sub.subId !== subId);

        if (filteredSubs.length === 0) {
          if (socket && socket.readyState === WebSocket.OPEN) {
            const mode = this.getMode(type);
            const unsubscribeMessage = {
              action: 'UNSUB',
              mode,
              entities: [`${path}`],
            };
            socket.send(JSON.stringify(unsubscribeMessage));
          } else {
            console.error(
              '[TradesWebSocketManager] Trades connection not initialized for unsubscribe',
              type,
            );
          }
          this.subscriptions.delete(subscriptionKey);
        } else {
          this.subscriptions.set(subscriptionKey, filteredSubs);
        }

        this.trackTradesEvent(WS_TRACKING_EVENTS.UNSUBSCRIBE_SUCCESS, {
          path,
          type,
          subId,
          subscriptionKey,
        });
      }
    } catch (error: unknown) {
      console.error(
        '[TradesWebSocketManager] Error unsubscribing from trades',
        error,
      );
      this.trackTradesEvent(WS_TRACKING_EVENTS.UNSUBSCRIBE_ERROR, {
        path,
        type,
        subId,
        subscriptionKey,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  subscribeOrders(path: TWebsocketPath, callback: TradesCallback): () => void {
    try {
      return this.subscribe(path, callback, TradeEventTypes.ORDER);
    } catch (error: unknown) {
      console.error(
        '[TradesWebSocketManager] Error subscribing to orders',
        error,
      );
      throw error;
    }
  }

  subscribePositions(
    path: TWebsocketPath,
    callback: TradesCallback,
  ): () => void {
    try {
      return this.subscribe(path, callback, TradeEventTypes.POSITION);
    } catch (error: unknown) {
      console.error(
        '[TradesWebSocketManager] Error subscribing to positions',
        error,
      );
      throw error;
    }
  }

  subscribeGTTOrders(
    path: TWebsocketPath,
    callback: TradesCallback,
  ): () => void {
    try {
      return this.subscribe(path, callback, TradeEventTypes.GTT_ORDER);
    } catch (error: unknown) {
      console.error(
        '[TradesWebSocketManager] Error subscribing to GTT orders',
        error,
      );
      throw error;
    }
  }

  subscribePositionRefresh(
    path: TWebsocketPath,
    callback: TradesCallback,
  ): () => void {
    try {
      return this.subscribe(path, callback, TradeEventTypes.POSITION_REFRESH);
    } catch (error: unknown) {
      console.error(
        '[TradesWebSocketManager] Error subscribing to position refresh',
        error,
      );
      throw error;
    }
  }

  isSubscribed(subscriptionKey: TSubscriptionKey): boolean {
    return this.subscriptions.has(subscriptionKey);
  }
}

export default TradesWebSocketManager;
